import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PlugsConnected, CircleNotch, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Sync-Preview modal: fetches the planned orders via dry_run=true, lets the
 * user veto individual rows with checkboxes, and only submits the checked
 * subset. Reduces the risk of firing bad market orders during off-hours or
 * on symbols the user isn't sure about.
 */
export default function SyncPreviewModal({ open, onClose, onSubmitted }) {
  const [planned, setPlanned] = useState([]);
  const [checked, setChecked] = useState({}); // symbol -> bool
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.post("/alpaca/positions/sync?dry_run=true", {})
      .then((r) => {
        setPlanned(r.data.planned || []);
        // Default all rows to checked so the "just do it" flow is one click.
        const next = {};
        (r.data.planned || []).forEach((p) => { next[p.symbol] = true; });
        setChecked(next);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not fetch planned orders");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [open, onClose]);

  const chosenSymbols = planned.filter((p) => checked[p.symbol]).map((p) => p.symbol);

  const submit = async () => {
    if (chosenSymbols.length === 0) {
      toast.info("Nothing selected — pick at least one order or close");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/alpaca/positions/sync", { symbols: chosenSymbols });
      const s = data.submitted?.length || 0;
      const f = data.failed?.length || 0;
      if (f === 0) toast.success(`Sync fired: ${s} order${s === 1 ? "" : "s"}`);
      else toast.warning(`${s} submitted, ${f} failed`);
      onSubmitted?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 max-w-lg text-white"
        data-testid="sync-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="mono">Preview sync orders</DialogTitle>
          <DialogDescription className="mono text-xs text-zinc-500">
            Uncheck any order you don't want to fire. Market orders go to your Alpaca paper account.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 mono text-xs text-zinc-500 py-6 justify-center">
            <CircleNotch size={14} className="animate-spin" /> Computing planned orders…
          </div>
        ) : planned.length === 0 ? (
          <div className="mono text-xs text-zinc-600 py-6 text-center">
            Nothing to sync — Alpaca is already in line with paper.
          </div>
        ) : (
          <>
            <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800/70 max-h-72 overflow-y-auto">
              {planned.map((p) => (
                <label
                  key={p.symbol}
                  data-testid={`sync-preview-row-${p.symbol}`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-900/40 cursor-pointer"
                >
                  <Checkbox
                    checked={!!checked[p.symbol]}
                    onCheckedChange={(v) => setChecked({ ...checked, [p.symbol]: !!v })}
                    data-testid={`sync-preview-check-${p.symbol}`}
                    className="border-zinc-600 data-[state=checked]:bg-white data-[state=checked]:text-black"
                  />
                  <div className="flex-1">
                    <div className="mono text-sm font-semibold">{p.symbol}</div>
                    <div className="mono text-[10px] text-zinc-500">
                      <span className={p.side === "buy" ? "profit" : "loss"}>{p.side.toUpperCase()}</span>{" "}
                      <span>{p.qty}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="p-2 rounded-sm border border-amber-500/30 bg-amber-500/5 mono text-[10px] text-amber-300 flex items-start gap-1.5">
              <Warning size={11} weight="bold" className="mt-0.5" />
              These fire immediately at market on your Alpaca account.
            </div>
          </>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            onClick={submit}
            disabled={submitting || loading || planned.length === 0 || chosenSymbols.length === 0}
            data-testid="sync-preview-submit"
            className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-sm mono text-[10px] uppercase tracking-widest gap-1.5 disabled:opacity-50"
          >
            {submitting ? <CircleNotch size={12} className="animate-spin" /> : <PlugsConnected size={12} weight="bold" />}
            {submitting ? "Firing…" : `Fire ${chosenSymbols.length} order${chosenSymbols.length === 1 ? "" : "s"}`}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            data-testid="sync-preview-cancel"
            className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
